import React from 'react';
import classes from '../../bidAnalysisTemplate.module.scss';

interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  status?: 'completed' | 'warning' | 'pending';
}

interface TemplateSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  navigationItems: NavigationItem[];
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
  templateType?: string;
  productType?: 'direct' | 'indirect' | null;
}

export const TemplateSidebar: React.FC<TemplateSidebarProps> = ({
  collapsed,
  onToggleCollapse,
  navigationItems,
  activeSection,
  onSectionClick,
  templateType,
  productType,
}) => {
  const getAnalysisTypeLabel = () => {
    if (templateType === 'nc') return 'NC';
    if (productType === 'direct') return 'MAS - Direct';
    if (productType === 'indirect') return 'MAS - Indirect';
    return 'MAS';
  };

  return (
    <div className={`${classes.sidebar} ${collapsed ? classes.collapsed : ''}`}>
      <div className={classes.sidebarHeader}>
        <h3>Navigation Menu</h3>
        <p className={classes.analysisType}>{getAnalysisTypeLabel()}</p>
        <button
          type="button"
          className={classes.sidebarToggle}
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
        >
          <i className={`pi ${collapsed ? 'pi-angle-right' : 'pi-angle-left'}`}></i>
        </button>
      </div>

      <nav className={classes.sidebarNav} aria-label="Bid analysis sections navigation">
        {navigationItems.map((item) => {
          // Determine which status icon to show (only in expanded mode to keep collapsed rail clean)
          let statusIcon: React.ReactNode = null;
          if (!collapsed) {
            if (item.status === 'completed') {
              statusIcon = (
                <i
                  className="pi pi-check"
                  style={{ color: '#10b981' }}
                  data-status="completed"
                  data-testid={`${item.id}-check`}
                ></i>
              );
            } else if (item.status === 'warning') {
              statusIcon = (
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ color: '#f59e0b', marginLeft: 'auto' }}
                  data-status="warning"
                  data-testid={`${item.id}-warning`}
                ></i>
              );
            } else if (item.status === 'pending') {
              statusIcon = (
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ color: '#f59e0b', marginLeft: 'auto' }}
                  data-status="pending"
                  data-testid={`${item.id}-pending`}
                ></i>
              );
            }
          }

          return (
            <div
              key={item.id}
              className={`${classes.navItem} ${activeSection === item.id ? classes.active : ''}`}
              onClick={() => onSectionClick(item.id)}
              data-item-id={item.id}
              data-item-status={item.status || 'undefined'}
              title={collapsed ? item.label : undefined}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSectionClick(item.id);
              }}
            >
              <i className={`pi ${item.icon}`} aria-hidden="true"></i>
              <span>{item.label}</span>
              {statusIcon}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

